/* DataRetentionComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./DataRetentionRenderer");
const _ = require("lodash");

class DataRetentionComponent extends EventEmitter {
    constructor(orgService, RetentionPolicy, userSession, uiUtils, steve, logger) {
        super();
        this._orgService = orgService;
        this._retentionPolicyEntity = RetentionPolicy;
        this._retentionPolicyOrgInstance = undefined;
        this._userSession = userSession;
        this._uiUtils = uiUtils;
        this._isSysOrgDataRetentionPolicy = false;
        this._isDataRetentionPolicyEnabled = false;
        this._isDataRetentionPolicyLocked = false;
        this._isSysOrgScheduleEnabled = true;
        this._renderer = new Renderer(this, this._uiUtils);
        this._steve = steve;
        this._logger = logger;

        this._schedule = undefined;
        this._scheduleEnabled = undefined;
        this._dataRetentionConfig = {};

        this._allRunStateComplete = false;

        this._updatePolicyData = this._updatePolicyData.bind(this);
        this._convertPolicyDataToSlickTableFormat =
            this._convertPolicyDataToSlickTableFormat.bind(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._retentionPolicyOrgInstance.on(
            "PropertiesUpdated",
            (this._propertiesUpdated = this._propertiesUpdated.bind(this))
        );
        this._retentionPolicyOrgInstance.on(
            "LastExecutionStatsPropertyUpdated",
            (this._executionStatsPropertyUpdated = this._executionStatsPropertyUpdated.bind(this))
        );
        this._retentionPolicyOrgInstance.on(
            "LastPreviewStatsPropertyUpdated",
            (this._previewStatsPropertyUpdated = this._previewStatsPropertyUpdated.bind(this))
        );
    }

    _unregisterListeners() {
        if (this._retentionPolicyOrgInstance) {
            this._retentionPolicyOrgInstance.removeAllListeners("PropertiesUpdated");
            this._retentionPolicyOrgInstance.removeAllListeners(
                "LastExecutionStatsPropertyUpdated"
            );
            this._retentionPolicyOrgInstance.removeAllListeners("LastPreviewStatsPropertyUpdated");
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let prom = this._renderer
            ._shutdown()
            .then(() => {
                if (!_.isUndefined(this._retentionPolicyOrgInstance)) {
                    return this._retentionPolicyOrgInstance.shutdown();
                } else {
                    return Promise.resolve();
                }
            })
            .then(
                () => {
                    this._retentionPolicyOrgInstance = undefined;
                    this._isDataRetentionPolicyEnabled = undefined;
                    this._isDataRetentionPolicyLocked = undefined;
                    this._isSysOrgScheduleEnabled = undefined;
                    this._scheduleEnabled = undefined;
                    this._renderer = undefined;
                    this._schedule = undefined;
                    this._dataRetentionConfig = undefined;
                },
                (err) => {
                    console.log("Error shutting down DataRetentionComponent: ", err);
                }
            );
        return prom;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getActiveOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    get isDataRetentionPolicyEnabled() {
        return this._isDataRetentionPolicyEnabled;
    }

    setDataRetentionPolicyEnabled(enabled) {
        this._isDataRetentionPolicyEnabled = enabled;
        return this;
    }

    get isDataRetentionPolicyLocked() {
        return this._isDataRetentionPolicyLocked;
    }

    setDataRetentionPolicyLocked(isLocked) {
        this._isDataRetentionPolicyLocked = isLocked;
        return this;
    }

    get dataRetentionConfigCopy() {
        return _.cloneDeep(this._dataRetentionConfig);
    }

    setDataRetentionConfig(data) {
        this._dataRetentionConfig = data;
    }

    get schedule() {
        return this._schedule;
    }

    setSchedule(newSchedule) {
        this._schedule = newSchedule;
        return this;
    }

    get scheduleEnabled() {
        return this._scheduleEnabled;
    }

    setScheduleEnabled(scheduleEnabled) {
        this._scheduleEnabled = scheduleEnabled;
        return this;
    }

    get allRunStateComplete() {
        return this._allRunStateComplete;
    }

    setAllRunStateComplete(newValue) {
        this._allRunStateComplete = newValue;
    }

    getPolicyData() {
        return this._retentionPolicyEntity.retrievePolicyDefaults().then((defaultPolicyArgs) => {
            // Got a deep copy of the object
            let defaultItems = _.cloneDeep(defaultPolicyArgs);

            // Flatten objects
            return this._retentionPolicyEntity
                .retrieveRetentionPolicy(this.activeOrgId)
                .then((retentionPolicy) => {
                    this._retentionPolicyOrgInstance = retentionPolicy;
                    this.setSchedule(retentionPolicy.schedule);
                    return this._retentionPolicyOrgInstance.isSysOrgDataRetentionPolicy();
                })
                .then((isSysOrgDataRetentionPolicy) => {
                    this._isSysOrgDataRetentionPolicy = !!isSysOrgDataRetentionPolicy;
                    return this._convertPolicyDataToSlickTableFormat(defaultItems);
                })
                .then((convertedDefaultItems) => {
                    this.setDataRetentionConfig(convertedDefaultItems);
                    this.setScheduleEnabled(this._retentionPolicyOrgInstance.scheduleEnabled);
                    this.setDataRetentionPolicyEnabled(
                        this._retentionPolicyOrgInstance.isAttached()
                    );
                    this.setDataRetentionPolicyLocked(
                        this._retentionPolicyOrgInstance.isLockedToOrg()
                    );
                    const policyArgs = _.cloneDeep(this._retentionPolicyOrgInstance.policyArgs);
                    const lastPreviewStats = _.get(
                        this._retentionPolicyOrgInstance,
                        "lastPreviewStats"
                    );
                    const lastExecutionStats = _.get(
                        this._retentionPolicyOrgInstance,
                        "lastExecutionStats"
                    );

                    if (_.isEmpty(policyArgs)) {
                        return Promise.resolve();
                    } else {
                        return this._updatePolicyData(policyArgs, lastPreviewStats).then(() => {
                            this._previewStatsPropertyUpdated(lastPreviewStats);
                            this._executionStatsPropertyUpdated(lastExecutionStats);
                            return Promise.resolve();
                        });
                    }
                })
                .catch((err) => {
                    console.log(
                        "Failed to retrieve retention policy for organization  '" +
                            this.activeOrgId +
                            "': ",
                        err
                    );
                    return Promise.reject();
                });
        });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    whenModelIsReady() {
        return this.getPolicyData().then(() => {
            this._registerListeners();
            return Promise.resolve();
        });
    }

    organizationChanged(orgObj) {
        this.emit("DataRetentionOrganizationChanged", orgObj);
    }

    _executionStatsPropertyUpdated(lastExecutionStats) {
        this._statsPropertyUpdated(lastExecutionStats, false);
    }

    _previewStatsPropertyUpdated(lastPreviewStats) {
        this._statsPropertyUpdated(lastPreviewStats, true);
    }

    _statsPropertyUpdated(statsProperty, isPreviewStatsUpdate = true) {
        const statsDataClasses = statsProperty.dataClass;

        let configDataClasses = [];
        let allRunStateComplete = true;

        _.forEach(this._dataRetentionConfig, (configDataClass) => {
            let dataClassId = configDataClass.objectKey,
                state = _.get(statsDataClasses, [dataClassId, "runState"], ""),
                mode = _.get(statsDataClasses, [dataClassId, "mode"]);

            // TODO - TEMPORARY PATCH - START - To be removed once platform_sdk / BE issue is resolved
            if (isPreviewStatsUpdate) {
                state =
                    configDataClass.usage === "Queued" && state === "complete" ? "queued" : state;
            } else {
                state =
                    _.get(configDataClass, "actions.state") === "Queued" && state === "complete"
                        ? "queued"
                        : state;
            }
            // TODO - TEMPORARY PATCH - END

            if (isPreviewStatsUpdate) {
                if (state === "complete") {
                    configDataClass.usage = _.capitalize(
                        _.get(
                            statsDataClasses,
                            [dataClassId, "discoverySummary", "discoveredCount"],
                            ""
                        )
                    );
                } else {
                    configDataClass.usage = _.capitalize(state);
                }
            } else {
                configDataClass.actions = {state: state, mode: mode};
            }

            configDataClasses.push(configDataClass);
            if (state === "running" || state === "queued") {
                allRunStateComplete = false;
            }
        });

        this.setDataRetentionConfig(configDataClasses);

        if (isPreviewStatsUpdate) {
            this.emit(
                "PreviewStatsPropertyUpdated",
                this.dataRetentionConfigCopy,
                allRunStateComplete
            );
        } else {
            this.emit(
                "ExecutionStatsPropertyUpdated",
                this.dataRetentionConfigCopy,
                allRunStateComplete
            );
        }
    }

    _propertiesUpdated(newProps, oldProps) {
        if (!this._uiUtils.isItemsMatch(newProps.policyArgs, oldProps.policyArgs)) {
            this._updatePolicyData(newProps.policyArgs, newProps.lastPreviewStats).then(() => {
                this.emit("DataRetentionPropertiesUpdated", newProps);
            });
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/
    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    _updatePolicyData(newPolicyData, lastPreviewStats, forceRunPreview = false) {
        return new Promise((resolve) => {
            if (this._dataRetentionConfig) {
                let updatedPolicyData = [];
                this._dataRetentionConfig.map((object) => {
                    const objKey = object.objectKey;
                    let newObject = object;
                    if (newPolicyData && newPolicyData[objKey]) {
                        newObject = Object.assign(object, newPolicyData[objKey]);
                    } else {
                        newObject = object;
                    }

                    newObject.usage = _.get(
                        lastPreviewStats,
                        ["dataClass", objKey, "discoverySummary", "discoveredCount"],
                        newObject.usage || ""
                    );

                    if (newObject.usage === "") {
                        delete newObject.usage;
                        // isCountPreviewEmpty = true;
                    }

                    let minAgeConstraint = _.parseInt(newObject.minAgeConstraint, 10);
                    minAgeConstraint = _.isNaN(minAgeConstraint) ? 0 : minAgeConstraint;

                    let age = _.parseInt(newObject.age, 10);
                    age = _.isNaN(age) ? 0 : age;

                    newObject.age = minAgeConstraint > age ? minAgeConstraint : age;

                    // Store age as a String, to compare values on SlickGrid changes(_setSavePolicyButtonAvailability method)
                    newObject.age = newObject.age.toString();
                    newObject.min = _.parseInt(newObject.min, 10);
                    newObject.min = _.isNaN(newObject.min) ? 0 : newObject.min;

                    updatedPolicyData.push(newObject);
                });

                return resolve([newPolicyData, updatedPolicyData]);
            }
        }).then((resolvedData) => {
            let [rawPolicyData, convertedPolicyData] = resolvedData;
            if (forceRunPreview) {
                this._retentionPolicyOrgInstance.runPreview(rawPolicyData);
            }
            this.setDataRetentionConfig(convertedPolicyData);
            return Promise.resolve();
        });
    }

    _convertPolicyDataToSlickTableFormat(policyArgs) {
        // Remove deletedOrgs and deletedInstances for non GluwareSystemOrganization
        return new Promise((resolve) => {
            let convertedData;
            if (!this._isSysOrgDataRetentionPolicy) {
                delete policyArgs.deletedOrgs;
                delete policyArgs.deletedInstances;
            }
            convertedData = Object.keys(policyArgs).map((key) => {
                const object = policyArgs[key];
                const defaults = object.defaults;
                defaults.age = object.minAgeConstraint
                    ? _.parseInt(object.minAgeConstraint, 10)
                    : _.parseInt(defaults.age, 10);
                const objectKey = {objectKey: key};
                delete object.defaults;
                return Object.assign(object, defaults, objectKey);
            });
            return resolve(convertedData);
        });
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = DataRetentionComponent;

DataRetentionComponent.$groupName = "Organization";

DataRetentionComponent.$displayName = "Data Retention";

DataRetentionComponent.$inject = [
    "$teve.services.instance.Organization",
    "$teve.entities.RetentionPolicy",
    "$teve.userSession",
    "$teve.ui.UiUtils",
    "$teve.core",
    "$teve.logger",
];
